/**
 * Debounce and throttle utilities for optimizing API calls
 * and user interactions
 */

/**
 * Debounce a function - delays execution until after a specified delay
 * has elapsed since the last time it was invoked.
 *
 * @param {Function} fn - The function to debounce
 * @param {number} delay - The delay in milliseconds (default: 300)
 * @returns {Function} The debounced function
 *
 * @example
 * const debouncedSearch = debounce(performSearch, 300);
 * searchInput.addEventListener('input', (e) => debouncedSearch(e.target.value));
 */
export function debounce(fn, delay = 300) {
  let timeoutId = null;

  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Throttle a function - ensures it's only called once per specified limit period.
 * The first call executes immediately, subsequent calls within the limit period
 * are scheduled to execute after the limit.
 *
 * @param {Function} fn - The function to throttle
 * @param {number} limit - The minimum time between executions in milliseconds (default: 1000)
 * @returns {Function} The throttled function
 *
 * @example
 * const throttledPoll = throttle(loadQueueStatus, 1000);
 * setInterval(throttledPoll, 500); // Will only execute max once per second
 */
export function throttle(fn, limit = 1000) {
  let lastRun = 0;
  let timeoutId = null;

  return function(...args) {
    const now = Date.now();

    if (now - lastRun >= limit) {
      fn.apply(this, args);
      lastRun = now;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fn.apply(this, args);
        lastRun = Date.now();
      }, limit - (now - lastRun));
    }
  };
}
